// AddNoteModal — one box for a note AND a touch.
//
// Until 2026-09-10 this was LogTouchModal: a 2-click "this happened"
// record for the at-risk engine (pick a touch type, optional one-line
// aside). Brian Ream wrote his meeting notes into it — "Isn't logging a
// touch how you enter in notes?" — and that question is the design: for
// the advisor they are one act. So this is the composer, and the touch
// type is an optional question inside it.
//
//   * Touch type picked  → writes the retention-touch ACTIVITY exactly as
//     before (POST /households/:id/touch), with the note as its body and
//     the chosen date as occurred_at. Credits relationship health,
//     updates "Last touch", shows in Notes & meetings.
//   * Nothing picked     → writes a plain NOTE on `noteTarget` (the record
//     the button sits on; the household when there is none), the same
//     path the Notes dock uses.
//
// Nothing behind the scenes changes — the two writes are the two that
// existed; only the front of the box is new. Mockup (approved shape):
// docs/archive/mockups/notes-and-touches-mockup-v1.html, stop 2.
//
// Minimum Viable Typing Category 3: the advisor already DID the work.
// What was said is the one thing only they can supply, so the writing
// space is sized for it, grows as they type, and cannot be lost: once
// anything is typed the backdrop no longer dismisses, X / Esc ask first,
// and the draft is kept in this tab (sessionStorage — the one home the
// localStorage rule allows for an in-progress composer draft) until it
// is saved or discarded.

import { StickyNote } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  api,
  ApiError,
  NOTE_KIND_BY_SEGMENT,
  newNoteId,
  noteSaveFailureMessage,
  type Note,
} from "../../api";
import type { NoteEntitySegment } from "@shared/types/notes";
import { Button, Modal } from "../../ui";
import { NotePiiWarning, draftHasPii } from "../NoteSurfaceHelpers";
import { VoiceNoteComposer } from "../VoiceNoteComposer";
import { emitNotesChanged } from "./notes-changed-event";
import { DateField } from "../DateField";

export interface AddNoteModalProps {
  open: boolean;
  householdId: string;
  householdName: string;
  /** Where a plain note (no touch picked) is written. Defaults to the
   *  household. A record page passes itself so a note written from its
   *  header lands on that record, exactly as the Notes dock would. */
  noteTarget?: { kind: NoteEntitySegment; entityId: string };
  /** Open with this touch chip already picked (the "Log a gift" button on
   *  the contact page passes "gift_sent"). Applied each time the box opens. */
  initialTouchType?: string;
  onClose: () => void;
  /** Called after a TOUCH is logged, with the written activity so a
   *  caller that offers Undo (the Financial dashboards' Needs-your-
   *  attention queue) has the id to soft-delete. */
  onLogged: (written: { activity_id: string; occurred_at: string }) => void;
  /** Called after a plain NOTE is saved (typed or spoken). Optional —
   *  callers that only care about touches (the dashboard queue) omit it. */
  onNoteSaved?: (note?: Note) => void;
}

/** Which record a touch is about when the box was opened on one — the
 *  touch then counts for that record and its company, not just the
 *  household (relevant touches, 2026-09-22). A household or contact page
 *  names no record, and the server derives the company on its own. */
export const TOUCH_RECORD_BY_SEGMENT: Record<
  NoteEntitySegment,
  "service_record" | "opportunity" | "transaction" | "rhythm_instance" | null
> = {
  households: null,
  contacts: null,
  "service-records": "service_record",
  opportunities: "opportunity",
  transactions: "transaction",
  "rhythm-instances": "rhythm_instance",
};

/** The retention touch types ("Texted" added 2026-09-24). The `hint`
 *  is what the line under the chip row says once one is picked. */
export const TOUCH_OPTIONS: ReadonlyArray<{
  type: string;
  label: string;
  hint: string;
}> = [
  { type: "meeting_in_person", label: "In-person meeting", hint: "office or client-site visit" },
  { type: "meeting_phone", label: "Phone conversation", hint: "a real conversation, not a voicemail" },
  { type: "meeting_video", label: "Video conversation", hint: "Zoom, Teams, FaceTime" },
  { type: "text_sent", label: "Texted", hint: "a text with the client, not a mass text" },
  { type: "card_sent", label: "Card sent", hint: "handwritten note or card" },
  { type: "gift_sent", label: "Gift sent", hint: "delivery confirmed" },
  { type: "event_attended", label: "Attended an event", hint: "client appreciation, workshop, etc." },
  {
    type: "life_event_acknowledged",
    label: "Life event acknowledged",
    hint: "personal outreach for a birth, move, loss, new job…",
  },
];

const MAX_NOTE_LENGTH = 8000;

function localDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function shiftDays(d: Date, days: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}

/** "Sep 10" for the preview line. */
function shortDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** The chosen day at local noon, as an ISO instant — noon so a date
 *  never rolls into the neighbouring day once stored in UTC. Today is
 *  sent as nothing at all, so the server stamps the exact current
 *  moment (the pre-2026-09-10 behavior). */
function occurredAtFor(dateStr: string, today: string): string | undefined {
  if (dateStr === today) return undefined;
  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d, 12, 0, 0, 0).toISOString();
}

function draftKey(householdId: string, target?: { kind: string; entityId: string }): string {
  return `add-note-draft:${householdId}:${target ? `${target.kind}:${target.entityId}` : "household"}`;
}

function readDraft(key: string): string {
  try {
    return window.sessionStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function writeDraft(key: string, value: string): void {
  try {
    if (value) window.sessionStorage.setItem(key, value);
    else window.sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable (private window, quota) — the draft still lives
    // in component state; only crash recovery is lost.
  }
}

export function AddNoteModal({
  open,
  householdId,
  householdName,
  noteTarget,
  initialTouchType,
  onClose,
  onLogged,
  onNoteSaved,
}: AddNoteModalProps) {
  const today = localDateString(new Date());
  const key = draftKey(householdId, noteTarget);

  const [touchType, setTouchType] = useState<string | null>(null);
  const [when, setWhen] = useState(today);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [piiAcknowledged, setPiiAcknowledged] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Same id on every attempt at this exact text, so a retry can't double
  // a note that landed without answering (Nate, 2026-09-22). Reset when
  // the words or the target change.
  const pendingNoteIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    pendingNoteIdRef.current = undefined;
  }, [note, noteTarget?.kind, noteTarget?.entityId, householdId]);

  // A caller can open the box with a chip already chosen.
  useEffect(() => {
    if (open && initialTouchType) setTouchType(initialTouchType);
  }, [open, initialTouchType]);

  // Restore a kept draft when the box opens for this record.
  useEffect(() => {
    if (!open) return;
    const kept = readDraft(key);
    if (kept) setNote(kept);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore once per open, not on every keystroke
  }, [open, key]);

  // Keep the draft in this tab while typing.
  useEffect(() => {
    if (!open) return;
    writeDraft(key, note);
  }, [open, key, note]);

  // Grow with the text — no resize handle to drag (that drag is what
  // used to close the box and lose the note).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [note, open]);

  const selected = useMemo(
    () => TOUCH_OPTIONS.find((o) => o.type === touchType) ?? null,
    [touchType],
  );
  const dirty = note.trim().length > 0;
  const noteTargetKind = noteTarget ? NOTE_KIND_BY_SEGMENT[noteTarget.kind] : "household";
  const noteTargetId = noteTarget?.entityId ?? householdId;
  const canSave =
    !saving && (selected !== null || dirty) && !(draftHasPii(note) && !piiAcknowledged);

  const reset = () => {
    setTouchType(null);
    setWhen(today);
    setNote("");
    setError(null);
    setPiiAcknowledged(false);
    setConfirmDiscard(false);
  };

  const closeDiscarding = () => {
    writeDraft(key, "");
    reset();
    onClose();
  };

  /** Every close path (X, Esc, Cancel) comes through here: with text in
   *  the box it asks first; empty, it just closes. */
  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmDiscard(true);
    else closeDiscarding();
  };

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      if (selected) {
        const occurredAt = occurredAtFor(when, today);
        const touchRecord = noteTarget ? TOUCH_RECORD_BY_SEGMENT[noteTarget.kind] : null;
        const written = await api.logHouseholdTouch(householdId, {
          activity_type: selected.type,
          ...(touchRecord && noteTarget
            ? { record_type: touchRecord, record_id: noteTarget.entityId }
            : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
          ...(occurredAt ? { occurred_at: occurredAt } : {}),
        });
        writeDraft(key, "");
        reset();
        onLogged(written);
      } else {
        pendingNoteIdRef.current ??= newNoteId();
        const res = await api.addEntityNote(noteTarget?.kind ?? "households", noteTargetId, {
          body: note.trim(),
          ...(pendingNoteIdRef.current ? { id: pendingNoteIdRef.current } : {}),
        });
        pendingNoteIdRef.current = undefined;
        emitNotesChanged({ entityType: noteTargetKind, entityId: noteTargetId });
        writeDraft(key, "");
        reset();
        onNoteSaved?.(res.note);
      }
      onClose();
    } catch (err) {
      // A touch is its own write and is not retried; a note has already
      // retried itself by the time it lands here.
      setError(
        selected
          ? err instanceof ApiError || err instanceof Error
            ? `${err.message} Your note is still here.`
            : "Couldn't save. Your note is still here. Tap Try again."
          : noteSaveFailureMessage(err),
      );
    } finally {
      setSaving(false);
    }
  };

  const previewLine = selected
    ? `Saves as a ${selected.label.toLowerCase()} with ${householdName} ${when === today ? "today" : `on ${shortDate(when)}`}. It shows in Notes & meetings, updates "Last touch," and counts as a touch for relationship health.`
    : dirty
      ? `Saves as a note on ${noteTarget ? "this record" : householdName}. It shows in Notes & meetings and in this record's Notes tab.`
      : null;

  return (
    <>
      <Modal
        open={open}
        onClose={requestClose}
        dismissOnBackdrop={!dirty}
        title="Add a note"
        icon={<StickyNote size={24} strokeWidth={1.5} aria-hidden />}
        sub={householdName}
        size="md"
        className="add-note-modal"
        footer={
          <>
            <Button variant="sw-default" onClick={requestClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="sw-primary"
              disabled={!canSave}
              onClick={() => void submit()}
              data-testid="add-note-save"
            >
              {saving ? "Saving…" : "Save note"}
            </Button>
          </>
        }
      >
        <div className="add-note__section">
          <div className="add-note__label-row">
            <h4 className="add-note__label">What kind of touch was this?</h4>
            <span className="add-note__label-hint">
              Optional. Pick one if you met, called, texted, or sent something.
            </span>
          </div>
          <div className="add-note__chips" role="group" aria-label="Kind of touch">
            {TOUCH_OPTIONS.map((o) => {
              const on = touchType === o.type;
              return (
                <button
                  key={o.type}
                  type="button"
                  className={`add-note__chip${on ? " add-note__chip--on" : ""}`}
                  aria-pressed={on}
                  onClick={() => setTouchType(on ? null : o.type)}
                >
                  <i aria-hidden />
                  {o.label}
                </button>
              );
            })}
          </div>
          <p className="add-note__hint">
            {selected ? (
              <>
                <strong>{selected.label}</strong> — {selected.hint}. Counts toward this
                household's relationship health.
              </>
            ) : (
              <>Nothing picked means a plain note. A touch is what resets "Last touch."</>
            )}
          </p>
        </div>

        {selected ? (
          <div className="add-note__section">
            <div className="add-note__label-row">
              <h4 className="add-note__label">When</h4>
            </div>
            <div className="add-note__when">
              <DateField
                id="add-note-when"
                className="add-note__date"
                aria-label="When it happened"
                value={when}
                max={today}
                onChange={(v) => setWhen(v || today)}
              />
              <button
                type="button"
                className="add-note__quick"
                onClick={() => setWhen(today)}
                aria-pressed={when === today}
              >
                Today
              </button>
              <button
                type="button"
                className="add-note__quick"
                onClick={() => setWhen(localDateString(shiftDays(new Date(), -1)))}
              >
                Yesterday
              </button>
            </div>
          </div>
        ) : null}

        <div className="add-note__section">
          <div className="add-note__label-row">
            <h4 className="add-note__label">
              {selected ? "What was discussed" : "The note"}
            </h4>
          </div>
          <textarea
            ref={textareaRef}
            id="add-note-body"
            className="add-note__textarea"
            aria-label={selected ? "What was discussed" : "The note"}
            placeholder={
              selected
                ? "What was discussed, what you promised, what's next."
                : `Add a note about ${noteTarget ? "this record" : householdName}: what was discussed, what you promised, what's next.`
            }
            rows={9}
            maxLength={MAX_NOTE_LENGTH}
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setPiiAcknowledged(false);
            }}
          />
          <div className="add-note__textarea-foot">
            <span>Grows as you type. Closing this box asks before it throws anything away.</span>
            {dirty ? <b>Draft kept</b> : null}
          </div>
          <NotePiiWarning text={note} />
          {draftHasPii(note) && !piiAcknowledged ? (
            <div className="add-note__pii-actions">
              <Button size="sm" onClick={() => setPiiAcknowledged(true)}>
                Save it here anyway
              </Button>
            </div>
          ) : null}
        </div>

        {previewLine ? (
          <p className="add-note__preview" data-testid="add-note-preview">
            {previewLine}
          </p>
        ) : null}

        {error ? (
          <p className="add-note__error" role="alert">
            {error}{" "}
            <Button size="sm" disabled={!canSave} onClick={() => void submit()}>
              Try again
            </Button>
          </p>
        ) : null}

        {/* Or speak it — record → Whisper → AI-structured note + tasks. The
            same composer the Notes tab carries; it writes its own note
            on the note target and reports back. A spoken note is a plain
            note (no touch) — say the touch in the note if it matters. */}
        <details className="add-note__voice">
          <summary>Speak it instead</summary>
          <VoiceNoteComposer
            entityType={noteTargetKind}
            entityId={noteTargetId}
            onSaved={() => {
              emitNotesChanged({ entityType: noteTargetKind, entityId: noteTargetId });
              onNoteSaved?.();
            }}
          />
        </details>
      </Modal>

      <Modal
        open={open && confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        title="Discard this note?"
        size="sm"
        footer={
          <>
            <Button variant="sw-danger" onClick={closeDiscarding} data-testid="add-note-discard">
              Discard note
            </Button>
            <Button variant="sw-primary" onClick={() => setConfirmDiscard(false)} autoFocus>
              Keep writing
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          You have a note written for {householdName}. Closing throws it away.
        </p>
      </Modal>
    </>
  );
}
